import { useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

export const useBarcodeScanner = () => {
  const navigate = useNavigate()
  const bufferRef = useRef('')
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (
        (e.target as HTMLElement).tagName === 'INPUT' ||
        (e.target as HTMLElement).tagName === 'TEXTAREA' ||
        (e.target as HTMLElement).getAttribute('role') === 'textbox' ||
        (e.target as HTMLElement).closest('[role="textbox"]') ||
        (e.target as HTMLElement).closest('input') ||
        (e.target as HTMLElement).closest('textarea')
      ) {
        return
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      bufferRef.current += e.key

      timeoutRef.current = setTimeout(() => {
        const input = bufferRef.current
        bufferRef.current = ''

        console.debug('🔍 Scan detected:', input)

        // Clean the input by removing common error characters
        const cleanedInput = input.replace(/Enter$/i, '').trim()

        // Standard UUID is 36 characters (32 hex digits + 4 hyphens)
        // We'll set a reasonable minimum length to filter out obviously invalid inputs
        // but still catch partial UUIDs that might be in a URL
        if (cleanedInput.length < 20) {
          console.warn('❌ Input too short for a UUID:', input)
          return
        }

        // First check for UUID in a URL pattern (highest priority)
        const urlPattern =
          /(?:\/)?app\/purchases\/ticket-library\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i
        const urlMatch = input.match(urlPattern)

        if (urlMatch) {
          console.debug('✅ Valid UUID in URL scan, navigating to:', urlMatch[1])
          navigate(`/app/purchases/ticket-library/${urlMatch[1]}`)
          return
        }

        // Check for valid UUID format
        const uuidPattern = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i
        const uuidMatch = cleanedInput.match(uuidPattern)

        if (uuidMatch) {
          console.debug('✅ Valid UUID scan, navigating to:', uuidMatch[1])
          navigate(`/app/purchases/ticket-library/${uuidMatch[1]}`)
          return
        }

        // Filter out common scanner error patterns and invalid inputs
        const errorPatterns = [
          /^0{4,}$/, // Sequence of 4 or more zeros
          /^7{4,}$/, // Sequence of 4 or more sevens
          /^(.)\1{3,}$/, // Any single character repeated 4 or more times
          /^Enter$/i, // Just "Enter"
          /^\s*$/, // Empty or whitespace only
          /^[?]+$/, // Just question marks
          /^E+$/, // Just E characters
          /Jump to/i, // Contains "Jump to"
          /^https?:\/\/[^/]+\/app\/purchases\/ticket-library\/\d+$/, // Numeric URLs (not UUIDs)
        ]

        if (errorPatterns.some(pattern => pattern.test(cleanedInput))) {
          console.warn('❌ Scanner error pattern detected:', input)
          return
        }

        // If we get here, the scan is not a valid UUID or URL with UUID
        console.warn('❌ Invalid scan format (not a UUID):', input)
        // Don't navigate for invalid formats
      }, 100)
    }

    window.addEventListener('keypress', handleKeyPress)

    return () => {
      window.removeEventListener('keypress', handleKeyPress)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [navigate])
}
